import type { OnDestroy, OnInit } from '@angular/core';
import type { FormControl, FormGroup } from '@angular/forms';
import type { ELanguages } from '@common/enums';
import type { Uuid } from '@common/types';
import type { LibraryBlockEntity, UnsavedLibraryBlockEntity } from '@web/store/library-blocks/library-blocks.models';

import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil, throttleTime, withLatestFrom } from 'rxjs/operators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

import {
    LibraryBlockCategory,
    libraryBlockCategoryTranslations,
    TempStatus,
} from '@web/store/library-blocks/library-blocks.models';
import { LibraryBlocksService } from '@web/store/library-blocks/library-blocks.service';

import { LibraryBlockFacade } from '@web/store/library-blocks/library-blocks.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { KeyValuePipe } from '@angular/common';

type BlockLibraryAddonComponentProps = {
    enabled: FormControl<boolean>;
    category: FormControl<LibraryBlockCategory>;
    name: FormControl<string>;
    blockId: FormControl<string>;
    lang: FormControl<ELanguages>;
    templateId: FormControl<Uuid | number>;
};

const NAME_MAX_LENGTH = 50;

@Component({
    selector: 'web-block-library-addon',
    templateUrl: './block-library-addon.component.html',
    styleUrls: ['./block-library-addon.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpSwitchComponent, ReactiveFormsModule, KeyValuePipe, TranslateModule],
})
export class BlockLibraryAddonComponent implements OnInit, OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly translateService = inject(TranslateService);
    private readonly libraryBlocksService = inject(LibraryBlocksService);
    private readonly libraryBlockFacade = inject(LibraryBlockFacade);
    private readonly siteFacade = inject(SiteFacade);

    @Input() public blockId: string;

    public form: FormGroup<BlockLibraryAddonComponentProps>;
    public categories;

    public libraryBlock: LibraryBlockEntity;
    public unsavedLibraryBlock: UnsavedLibraryBlockEntity;

    private readonly onDestroy$: Subject<void> = new Subject();

    constructor() {
        this.categories = libraryBlockCategoryTranslations(this.translateService);
        delete this.categories[LibraryBlockCategory.widgets];

        this.form = this.fb.group<BlockLibraryAddonComponentProps>({
            enabled: this.fb.control(false),
            category: this.fb.control(null, { validators: [Validators.required] }),
            name: this.fb.control(null, { validators: [Validators.required, Validators.maxLength(50)] }),
            blockId: this.fb.control(null, { validators: [Validators.required] }),
            lang: this.fb.control(null, { validators: [Validators.required] }),
            templateId: this.fb.control(null, { validators: [Validators.required] }),
        });
    }

    public ngOnInit(): void {
        this.setFormData();

        this.form.valueChanges
            .pipe(takeUntil(this.onDestroy$), debounceTime(300), throttleTime(300))
            .subscribe((val) => {
                if (val.enabled) {
                    this.libraryBlockFacade.upsertLibraryBlock({
                        categoryIds: [val.category],
                        name: val.name,
                        blockId: this.blockId,
                    } as UnsavedLibraryBlockEntity);
                }
            });

        this.libraryBlockFacade
            .libraryBlock$(this.blockId)
            .pipe(
                takeUntil(this.onDestroy$),
                filter((block) => Boolean(block)),
                take(1)
            )
            .subscribe((block: any) => {
                if ('id' in block) {
                    this.libraryBlock = block;
                } else {
                    this.unsavedLibraryBlock = block;
                }

                this.form.patchValue(
                    {
                        ...block,
                        category: block.categoryIds[0],
                        enabled: block?.tempStatus !== TempStatus.deleted,
                    },
                    { emitEvent: false }
                );
            });
    }

    public setFormData() {}

    public createBlock(): void {
        this.siteFacade.lang$.pipe(take(1), withLatestFrom(this.siteFacade.id$)).subscribe(([lang, websiteId]) =>
            this.libraryBlockFacade.upsertLibraryBlock(
                this.libraryBlocksService.createDefaultBlockEntity({
                    templateId: websiteId,
                    blockId: this.blockId,
                    lang,
                })
            )
        );
    }

    public removeBlock(): void {
        this.form.reset({}, { emitEvent: false });

        if (this.libraryBlock && 'id' in this.libraryBlock) {
            this.libraryBlockFacade.markLibraryBlockAsDeleted(this.blockId);
            return;
        }

        this.libraryBlockFacade.removeLibraryBlock(this.blockId);
    }

    public setEnabled(value: boolean): void {
        this.enabled.setValue(value, { onlySelf: true });

        if (value) {
            return this.createBlock();
        }

        return this.removeBlock();
    }

    public setCategory(event: Event): void {
        const selectedOption = (event.target as HTMLSelectElement).value;

        this.category.setValue(parseInt(selectedOption, 10));
    }

    public get nameMaxLength(): number {
        return NAME_MAX_LENGTH;
    }

    public get name(): BlockLibraryAddonComponentProps['name'] {
        return this.form.get('name') as BlockLibraryAddonComponentProps['name'];
    }

    public get enabled(): BlockLibraryAddonComponentProps['enabled'] {
        return this.form.get('enabled') as BlockLibraryAddonComponentProps['enabled'];
    }

    public get category(): BlockLibraryAddonComponentProps['category'] {
        return this.form.get('category') as BlockLibraryAddonComponentProps['category'];
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
